import React, { useEffect, useMemo, useState } from "react";
import {
    Group,
    Text,
    Badge,
    Button,
    Stack,
    Loader,
    Center,
    Divider,
    Avatar,
    Paper,
    ActionIcon,
    Tooltip,
    Collapse,
    Modal,
    TextInput,
    FileButton,
    Image,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
    IconCalendarEvent,
    IconClock,
    IconListCheck,
    IconUsers,
    IconPencil,
    IconChevronDown,
    IconChevronUp,
} from "@tabler/icons-react";
import { Select } from "@mantine/core";
import "./EventUserDashboard.css";
import { getAuthHeaders } from "../api/authHelper";

type Language = "da" | "en";

interface MyShiftItem {
    shiftId: number;
    groupId: number;
    start: string;
    end: string;
    status: string;
    title?: string;
    titleDK?: string;
    titleEN?: string;
    eventId?: number;
    functionId?: number;
    functionName?: string;
    endsOneHourAfterPlay: boolean;
}

interface MyShiftResponse {
    confirmed: MyShiftItem[];
    waitlist: MyShiftItem[];
    stats: {
        confirmedCount: number;
        waitlistCount: number;
    };
}

function fmtDateTime(iso: string, locale: string) {
    try {
        return new Date(iso).toLocaleString(locale, {
            weekday: "short",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function canUnregister(startIso: string, now = new Date()) {
    const start = new Date(startIso).getTime();
    const diffHours = (start - now.getTime()) / 36e5;
    return diffHours >= 24;
}

function hoursBetween(startIso: string, endIso: string) {
    const a = new Date(startIso).getTime();
    const b = new Date(endIso).getTime();
    if (Number.isNaN(a) || Number.isNaN(b)) return 0;
    return Math.max(0, (b - a) / 36e5);
}

function formatHours(hours: number, language: "da" | "en" = "da"): string {
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    if (language === "en") {
        if (h > 1) {
            return m === 0 ? `${h} hours` : `${h} hours and ${m} minutes`;
        } else {
            return m === 0 ? `${h} hour` : `${h} hour and ${m} minutes`;
        }
    }

    if (h > 1) {
        return m === 0 ? `${h} timer` : `${h} timer og ${m} minutter`;
    } else {
        return m === 0 ? `${h} time` : `${h} time og ${m} minutter`;
    }
}

function StatBox(props: { icon: React.ReactNode; label: string; value: string; hint?: string }) {
    return (
        <Paper className="stat" p="md">
            <Group justify="space-between" align="flex-start">
                <Stack gap={3}>
                    <Text c="dimmed" size="sm">
                        {props.label}
                    </Text>
                    <Text fw={900} size="xl">
                        {props.value}
                    </Text>
                    {props.hint && (
                        <Text c="dimmed" size="xs">
                            {props.hint}
                        </Text>
                    )}
                </Stack>

                <div className="iconBubble">{props.icon}</div>
            </Group>
        </Paper>
    );
}

type Props = {
    eventId?: number;
    language: Language;
    onChanged?: () => void;
    defaultCollapsed?: boolean;
    refreshKey?: number;
};

const toImageUrl = (path?: string | null) => {
    if (!path) return "";
    if (path.startsWith("data:image")) return path;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;

    const base = process.env.REACT_APP_WEB_URL || window.location.origin;
    return `${base}${path.startsWith("/") ? path : `/${path}`}`;
};

const EventUserDashboard: React.FC<Props> = ({
    eventId,
    language,
    onChanged,
    defaultCollapsed = false,
    refreshKey = 0,
}) => {
    const apiUrl = process.env.REACT_APP_API_URL;

    const personId = useMemo(() => {
        const v =
            sessionStorage.getItem("personId") ||
            sessionStorage.getItem("PersonID") ||
            sessionStorage.getItem("personID");
        return v ? Number(v) : 0;
    }, []);

    const userId = Number(sessionStorage.getItem("userId") || 0);
    const username = useMemo(() => sessionStorage.getItem("username") || "", []);
    const userEmail = useMemo(() => sessionStorage.getItem("email") || sessionStorage.getItem("userEmail") || "", []);
    const userAvatarUrlSession = useMemo(() => sessionStorage.getItem("avatarUrl") || "", []);
    const firstNameSession = useMemo(() => sessionStorage.getItem("firstName") || "", []);

    const [countries, setCountries] = useState<{ code: string; name: string }[]>([]);
    const [data, setData] = useState<MyShiftResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [busyShiftId, setBusyShiftId] = useState<number | null>(null);
    const [eventNames, setEventNames] = useState<Record<number, string>>({});

    const storageKey = eventId ? `eventDashboardCollapsed_${eventId}` : `profileDashboardCollapsed`;
    const [collapsed, setCollapsed] = useState<boolean>(() => {
        const saved = localStorage.getItem(storageKey);
        if (saved === null) return defaultCollapsed;
        return saved === "1";
    });

    const [editOpen, setEditOpen] = useState(false);
    const [mandatoryUpdateOpen, setMandatoryUpdateOpen] = useState(false);

    const [editFirstName, setEditFirstName] = useState(firstNameSession || "");
    const [editEmail, setEditEmail] = useState(userEmail || "");
    const [editLastName, setEditLastName] = useState("");
    const [editPhone, setEditPhone] = useState("");
    const [editDateOfBirth, setEditDateOfBirth] = useState<string | null>(null);
    const [editCountry, setEditCountry] = useState<string | null>(null);
    const [personFirstName, setPersonFirstName] = useState("");

    const [editAvatar, setEditAvatar] = useState<string>("");
    const [avatarPreview, setAvatarPreview] = useState<string>(toImageUrl(userAvatarUrlSession || ""));
    const [currentAvatarUrl, setCurrentAvatarUrl] = useState<string>(toImageUrl(userAvatarUrlSession || ""));

    const locale = language === "da" ? "da-DK" : "en-US";

    useEffect(() => {
        const fetchCountries = async () => {
            if (!apiUrl) return;
            try {
                const res = await fetch(`${apiUrl}/api/countries`);
                if (!res.ok) throw new Error("Failed to fetch countries");
                const data = await res.json();
                setCountries(data);
            } catch (err) {
                console.error("Failed to fetch countries", err);
            }
        };
        fetchCountries();
    }, [apiUrl]);

    function toggleCollapsed() {
        setCollapsed((prev) => {
            const next = !prev;
            localStorage.setItem(storageKey, next ? "1" : "0");
            return next;
        });
    }

    const handleAvatarChange = (file: File | null) => {
        if (!file) {
            setEditAvatar("");
            setAvatarPreview(currentAvatarUrl || toImageUrl(userAvatarUrlSession || ""));
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result as string;
            setEditAvatar(base64);
            setAvatarPreview(base64);
        };
        reader.readAsDataURL(file);
    };

    async function load() {
        if (!apiUrl || !personId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const url = eventId
                ? `${apiUrl}/api/shifts/my?personId=${personId}&eventId=${eventId}`
                : `${apiUrl}/api/shifts/my?personId=${personId}`;

            const res = await fetch(url, {
                headers: getAuthHeaders(),
            });;
            if (!res.ok) throw new Error("Failed to fetch my shifts");

            const json = (await res.json()) as MyShiftResponse;
            setData(json);

            const allShifts = [
                ...(json.confirmed ?? []),
                ...(json.waitlist ?? []),
            ];

            const eventIds = Array.from(
                new Set(
                    allShifts
                        .map(x => x.eventId)
                        .filter((id): id is number => !!id)
                )
            );

            const names: Record<number, string> = {};

            await Promise.all(
                eventIds.map(async (id) => {
                    try {
                        const eventRes = await fetch(`${apiUrl}/api/events/${id}/light`, {
                            headers: getAuthHeaders(),
                        });

                        if (!eventRes.ok) return;

                        const eventJson = await eventRes.json();
                        names[id] = eventJson.eventName;
                    } catch (err) {
                        console.error(`Could not load event ${id}`, err);
                    }
                })
            );

            setEventNames(names);

        } catch {
            notifications.show({
                title: language === "da" ? "Fejl" : "Error",
                message: language === "da" ? "Kunne ikke hente dine vagter." : "Could not load your shifts.",
                color: "red",
            });
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl, eventId, personId, refreshKey]);


    async function unregister(shiftId: number) {
        if (!apiUrl) return;

        setBusyShiftId(shiftId);
        try {
            const res = await fetch(`${apiUrl}/api/shifts/${shiftId}/unregister`, {
                method: "DELETE",
                headers: getAuthHeaders({
                    "Content-Type": "application/json",
                }),
                body: JSON.stringify({ personId }),
            });

            if (!res.ok) throw new Error("Unregister failed");

            notifications.show({
                title: language === "da" ? "Afmeldt" : "Unregistered",
                message: language === "da" ? "Du er nu afmeldt vagten." : "You are now unregistered from the shift.",
                color: "green",
            });

            await load();
            onChanged?.();
        } catch {
            notifications.show({
                title: language === "da" ? "Fejl" : "Error",
                message: language === "da" ? "Kunne ikke afmelde vagten." : "Could not unregister from the shift.",
                color: "red",
            });
        } finally {
            setBusyShiftId(null);
        }
    }

    async function loadProfile() {
        if (!apiUrl || !personId) return;

        try {
            const resPerson = await fetch(`${apiUrl}/api/persons/${personId}`, {
                headers: getAuthHeaders({ "Content-Type": "application/json" }),
            });

            if (!resPerson.ok) {
                throw new Error("Could not load person");
            }
            const personJson = await resPerson.json();

            const fn =
                personJson.firstName && personJson.firstName !== ""
                    ? personJson.firstName
                    : firstNameSession;

            setEditFirstName(fn);
            setPersonFirstName(fn);
            setEditLastName(personJson.lastName || "");
            setEditDateOfBirth(personJson.dateOfBirth ? personJson.dateOfBirth.slice(0, 10) : null);
            setEditCountry(personJson.country || "");

            const imagePath = personJson.personImagePath || userAvatarUrlSession || "";
            setAvatarPreview(toImageUrl(imagePath));
            setCurrentAvatarUrl(toImageUrl(imagePath));
            setEditAvatar("");

            const resUser = await fetch(`${apiUrl}/api/users/${userId}`, {
                headers: getAuthHeaders({ "Content-Type": "application/json" }),
            });

            if (!resUser.ok) {
                throw new Error("Could not load user");
            }

            const userJson = await resUser.json();
            setEditPhone(userJson.phone || "");
            setEditEmail(userJson.email || userJson.Email || userEmail);
        } catch (error) {
            notifications.show({
                title: language === "da" ? "Fejl" : "Error",
                message: language === "da" ? "Kunne ikke hente profilen" : "Could not load profile",
                color: "red",
            });
        }
    }

    async function saveProfile(
        closeModalAfterSave: boolean,
        clearMandatoryFlag: boolean
    ) {
        try {
            // -----------------------------
            // UPDATE USER
            // -----------------------------
            const userRes = await fetch(`${apiUrl}/api/users/${userId}`, {
                method: "PUT",
                headers: getAuthHeaders({
                    "Content-Type": "application/json",
                }),
                body: JSON.stringify({
                    UserId: userId,
                    Email: editEmail,
                    FirstName: editFirstName,
                    LastName: editLastName,
                    Phone: editPhone,
                }),
            });

            if (!userRes.ok) {
                const errorText = await userRes.text();

                console.error(
                    "USER UPDATE ERROR:",
                    userRes.status,
                    errorText
                );

                throw new Error(
                    `Failed to update user: ${userRes.status} ${errorText}`
                );
            }

            // -----------------------------
            // UPDATE PERSON
            // -----------------------------
            const personRes = await fetch(`${apiUrl}/api/persons/${personId}`, {
                method: "PUT",
                headers: getAuthHeaders({
                    "Content-Type": "application/json",
                }),
                body: JSON.stringify({
                    PersonId: Number(personId),
                    FirstName: personFirstName || editFirstName,
                    LastName: editLastName,
                    DateOfBirth: editDateOfBirth || null,
                    Country: editCountry || null,

                    Title: null,
                    Company: null,
                    PersonImagePath: null,
                    UploadImageBase64: editAvatar || null,

                    events: [],
                    functions: [],
                }),
            });
            // -----------------------------
            // UPDATED PERSON RESPONSE
            // -----------------------------
            const updatedPerson = await personRes.json();

            const newImagePath =
                updatedPerson?.personImagePath ||
                currentAvatarUrl ||
                "";

            setCurrentAvatarUrl(toImageUrl(newImagePath));
            setAvatarPreview(toImageUrl(newImagePath));
            setEditAvatar("");

            if (updatedPerson?.personImagePath) {
                sessionStorage.setItem(
                    "avatarUrl",
                    updatedPerson.personImagePath
                );
            }

            // -----------------------------
            // SUCCESS NOTIFICATION
            // -----------------------------
            notifications.show({
                title: language === "da" ? "Gemt" : "Saved",
                message:
                    language === "da"
                        ? "Din profil er opdateret."
                        : "Your profile has been updated.",
                color: "green",
            });

            // -----------------------------
            // CLEAR MANDATORY PROFILE FLAG
            // -----------------------------
            if (clearMandatoryFlag) {
                sessionStorage.setItem("requireProfileUpdate", "0");
                setMandatoryUpdateOpen(false);
            }

            // -----------------------------
            // CLOSE EDIT MODAL
            // -----------------------------
            if (closeModalAfterSave) {
                setEditOpen(false);
            }

            // -----------------------------
            // RELOAD DATA
            // -----------------------------
            await load();

            onChanged?.();
        } catch (error) {
            console.error("SAVE PROFILE ERROR:", error);

            notifications.show({
                title: language === "da" ? "Fejl" : "Error",
                message:
                    language === "da"
                        ? "Kunne ikke opdatere profilen."
                        : "Could not update profile.",
                color: "red",
            });
        }
    }

    useEffect(() => {
        const requireUpdate = sessionStorage.getItem("requireProfileUpdate") === "1";

        if (requireUpdate) {
            (async () => {
                await loadProfile();
                setMandatoryUpdateOpen(true);
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return (
            <Center mt="md">
                <Loader />
            </Center>
        );
    }

    if (!personId) {
        return (
            <Paper className="wrapper" p="lg">
                <Text>{language === "da" ? "Log ind for at se dit overblik." : "Log in to see your dashboard."}</Text>
            </Paper>
        );
    }

    const confirmed = data?.confirmed ?? [];
    const waitlist = data?.waitlist ?? [];
    const totalHours = confirmed.reduce((sum, s) => sum + hoursBetween(s.start, s.end), 0);

    const nextShift =
        confirmed
            .slice()
            .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
            .find((s) => new Date(s.start).getTime() >= Date.now()) ?? confirmed[0];

    const displayName = `${username}`.trim() || "User";

    return (
        <div className="dashContainer">
            <div className="wrapper">
                <div className="top">
                    <Group justify="space-between" align="flex-start" wrap="wrap">
                        <Group gap="md">
                            <Avatar className="avatar" src={currentAvatarUrl || undefined} radius="xl" size={56}>
                                {displayName.slice(0, 2).toUpperCase()}
                            </Avatar>

                            <Stack gap={2}>
                                <Text className="name" size="lg">
                                    {language === "da" ? "Mit overblik" : "My overview"}
                                </Text>
                                <Text className="meta">
                                    {displayName}
                                    {userEmail ? ` · ${userEmail}` : ""}
                                </Text>

                                <Group gap="xs">
                                    <Badge className="badgeRed">
                                        {language === "da" ? "Tilmeldte" : "Confirmed"}: {confirmed.length}
                                    </Badge>
                                    <Badge variant="light">
                                        {language === "da" ? "Venteliste" : "Waitlist"}: {waitlist.length}
                                    </Badge>

                                    <Tooltip
                                        label={
                                            collapsed
                                                ? language === "da"
                                                    ? "Vis overblik"
                                                    : "Show"
                                                : language === "da"
                                                    ? "Skjul overblik"
                                                    : "Hide"
                                        }
                                    >
                                        <ActionIcon variant="subtle" color="gray" radius="xl" onClick={toggleCollapsed}>
                                            {collapsed ? <IconChevronDown size={18} /> : <IconChevronUp size={18} />}
                                        </ActionIcon>
                                    </Tooltip>
                                </Group>
                            </Stack>
                        </Group>

                        <Group gap="xs">
                            <Tooltip label={language === "da" ? "Rediger profil" : "Edit profile"}>
                                <ActionIcon
                                    variant="light"
                                    color="red"
                                    radius="xl"
                                    onClick={async () => {
                                        await loadProfile();
                                        setEditOpen(true);
                                    }}
                                >
                                    <IconPencil size={18} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    </Group>
                </div>

                <Collapse in={!collapsed}>
                    <div className="content">
                        <div className="nextShiftHero">
                            <div className="nextShiftLeft">
                                <div className="nextShiftKicker">{language === "da" ? "Næste vagt" : "Next shift"}</div>
                                <div className="nextShiftValue">{nextShift ? fmtDateTime(nextShift.start, locale) : "—"}</div>
                                <div className="nextShiftHint">
                                    {nextShift
                                        ? language === "da"
                                            ? nextShift.title || `#${nextShift.shiftId}`
                                            : nextShift.title || `#${nextShift.shiftId}`
                                        : language === "da"
                                            ? "Ingen vagter endnu"
                                            : "No shifts yet"}
                                </div>
                            </div>

                            <div className="nextShiftIcon">
                                <IconCalendarEvent size={22} />
                            </div>
                        </div>

                        <div className="statsGrid3">
                            <StatBox
                                icon={<IconListCheck size={18} />}
                                label={language === "da" ? "Tilmeldte vagter" : "Confirmed shifts"}
                                value={`${confirmed.length}`}
                                hint={language === "da" ? "Dine aktive tilmeldinger" : "Your active signups"}
                            />
                            <StatBox
                                icon={<IconUsers size={18} />}
                                label={language === "da" ? "Venteliste" : "Waitlist"}
                                value={`${waitlist.length}`}
                                hint={language === "da" ? "Du står i kø" : "You are queued"}
                            />
                            <StatBox
                                icon={<IconClock size={18} />}
                                label={language === "da" ? "Timer i alt" : "Total hours"}
                                value={formatHours(totalHours, language)}
                                hint={language === "da" ? "Beregnet ud fra dine vagter" : "Calculated from shifts"}
                            />
                        </div>

                        <Divider my="md" />

                        <div className="twoCols">
                            <div>
                                <Text className="sectionTitle" mb="xs">
                                    {language === "da" ? "Mine vagter" : "My shifts"}
                                </Text>

                                {confirmed.length === 0 ? (
                                    <Text c="dimmed">
                                        {language === "da"
                                            ? "Du har ingen tilmeldte vagter endnu."
                                            : "You have no confirmed shifts yet."}
                                    </Text>
                                ) : (
                                    <Stack gap="sm">
                                        {confirmed.map((s) => {
                                            const canCancel = canUnregister(s.start);
                                            return (
                                                <div key={s.shiftId} className="shiftCard">
                                                    <Group justify="space-between" align="flex-start">
                                                        <Stack gap={2}>
                                                            <Badge className="eventBadge">
                                                                {s.eventId ? eventNames[s.eventId] || `Event #${s.eventId}` : "Event"}
                                                            </Badge>
                                                            <Text className="shiftTitle">
                                                                {language === "da"
                                                                    ? s.title || `Vagt #${s.shiftId}`
                                                                    : s.title || `Shift #${s.shiftId}`}
                                                            </Text>
                                                            {s.functionName && (
                                                                <Text size="xs" c="dimmed">
                                                                    {s.functionName}
                                                                </Text>
                                                            )}
                                                            <Text className="shiftTime">
                                                                {fmtDateTime(s.start, locale)} –{" "}
                                                                {s.endsOneHourAfterPlay
                                                                    ? (language === "da"
                                                                        ? "1 time efter sidste kamp"
                                                                        : "1 hour after last match")
                                                                    : fmtDateTime(s.end, locale)}
                                                            </Text>
                                                        </Stack>

                                                        <Group gap="xs" wrap="nowrap">
                                                            <Badge className="statusBadge">
                                                                {language === "da" ? "Tilmeldt" : "Confirmed"}
                                                            </Badge>

                                                            <Tooltip
                                                                disabled={!canCancel || busyShiftId === s.shiftId}
                                                                label={
                                                                    language === "da"
                                                                        ? "Du skal melde afbud minimum 24 timer før din vagt."
                                                                        : "You must cancel at least 24 hours before your shift."
                                                                }
                                                            >
                                                                <span>
                                                                    <Button
                                                                        size="xs"
                                                                        color="red"
                                                                        variant="light"
                                                                        radius="md"
                                                                        loading={busyShiftId === s.shiftId}
                                                                        disabled={!canCancel || busyShiftId === s.shiftId}
                                                                        onClick={() => unregister(s.shiftId)}
                                                                    >
                                                                        {language === "da" ? "Afmeld" : "Unregister"}
                                                                    </Button>
                                                                </span>
                                                            </Tooltip>
                                                        </Group>
                                                    </Group>
                                                </div>
                                            );
                                        })}
                                    </Stack>
                                )}
                            </div>

                            <div>
                                <Text className="sectionTitle" mb="xs">
                                    {language === "da" ? "Venteliste" : "Waitlist"}
                                </Text>

                                {waitlist.length === 0 ? (
                                    <Text c="dimmed">
                                        {language === "da"
                                            ? "Du står ikke på venteliste til nogen vagter."
                                            : "You are not on any waitlist."}
                                    </Text>
                                ) : (
                                    <Stack gap="sm">
                                        {waitlist.map((s) => (
                                            <div key={`w-${s.shiftId}`} className="shiftCard">
                                                <Group justify="space-between" align="flex-start" wrap="wrap">
                                                    <Stack gap={2}>
                                                        <Text className="shiftTitle">
                                                            {language === "da"
                                                                ? s.title || `Vagt #${s.shiftId}`
                                                                : s.title || `Shift #${s.shiftId}`}
                                                        </Text>

                                                        <Text className="shiftTime">
                                                            {fmtDateTime(s.start, locale)} → {fmtDateTime(s.end, locale)}
                                                        </Text>
                                                    </Stack>
                                                    <Badge className="eventBadge">
                                                        {s.eventId ? eventNames[s.eventId] || `Event #${s.eventId}` : "Event"}
                                                    </Badge>
                                                    <Badge className="waitBadge">{language === "da" ? "Venteliste" : "Waitlist"}</Badge>
                                                </Group>
                                            </div>
                                        ))}
                                    </Stack>
                                )}
                            </div>
                        </div>
                    </div>
                </Collapse>

                <Modal
                    opened={editOpen}
                    onClose={() => setEditOpen(false)}
                    title={language === "da" ? "Rediger profil" : "Edit profile"}
                    centered
                >
                    <Stack>
                        <TextInput label="Email" value={editEmail} disabled />
                        <TextInput label={language === "da" ? "Fornavn" : "Firstname"} value={editFirstName} disabled />

                        <TextInput
                            label={language === "da" ? "Efternavn" : "Lastname"}
                            value={editLastName}
                            onChange={(e) => setEditLastName(e.currentTarget.value)}
                        />
                        <TextInput
                            label={language === "da" ? "Telefon" : "Phone"}
                            value={editPhone}
                            onChange={(e) => setEditPhone(e.currentTarget.value)}
                        />
                        <TextInput
                            label={language === "da" ? "Fødselsdato" : "Date of Birth"}
                            type="date"
                            value={editDateOfBirth || ""}
                            onChange={(e) => setEditDateOfBirth(e.currentTarget.value)}
                        />
                        <Select
                            label={language === "da" ? "Land" : "Country"}
                            placeholder={language === "da" ? "Vælg land" : "Select country"}
                            value={editCountry || ""}
                            onChange={setEditCountry}
                            data={countries.map(c => ({ value: c.code, label: c.name || c.code }))}
                        />

                        <Stack gap={8}>
                            <Text fw={700} size="sm">
                                {language === "da" ? "Profilbillede" : "Profile picture"}
                            </Text>

                            {avatarPreview ? (
                                <Image src={avatarPreview} alt="avatar preview" radius="md" h={140} fit="cover" />
                            ) : (
                                <Text c="dimmed" size="sm">
                                    {language === "da" ? "Ingen billede valgt." : "No image selected."}
                                </Text>
                            )}

                            <Group>
                                <FileButton onChange={handleAvatarChange} accept="image/png,image/jpeg,image/webp">
                                    {(props) => (
                                        <Button {...props} variant="light" color="gray">
                                            {language === "da" ? "Vælg billede" : "Choose image"}
                                        </Button>
                                    )}
                                </FileButton>

                                {editAvatar && (
                                    <Button
                                        variant="subtle"
                                        color="gray"
                                        onClick={() => {
                                            setEditAvatar("");
                                            setAvatarPreview(currentAvatarUrl || toImageUrl(userAvatarUrlSession || ""));
                                        }}
                                    >
                                        {language === "da" ? "Fortryd" : "Remove"}
                                    </Button>
                                )}
                            </Group>
                        </Stack>

                        <Group justify="flex-end" mt="sm">
                            <Button variant="default" onClick={() => setEditOpen(false)}>
                                {language === "da" ? "Annuller" : "Cancel"}
                            </Button>

                            <Button color="red" onClick={() => saveProfile(true, false)}>
                                {language === "da" ? "Gem" : "Save"}
                            </Button>
                        </Group>
                    </Stack>
                </Modal>

                <Modal
                    opened={mandatoryUpdateOpen}
                    onClose={() => { }}
                    closeOnClickOutside={false}
                    closeOnEscape={false}
                    title={language === "da" ? "Opdater din profil" : "Update your profile"}
                    centered
                >
                    <Stack>
                        <Text c="red" fw={700}>
                            {language === "da"
                                ? "Din profil er ældre end 6 måneder. Du skal opdatere dine oplysninger for at fortsætte."
                                : "Your profile is older than 6 months. Please update your information to continue."}
                        </Text>

                        <TextInput
                            label={language === "da" ? "Efternavn" : "Lastname"}
                            value={editLastName}
                            onChange={(e) => setEditLastName(e.currentTarget.value)}
                        />
                        <TextInput
                            label={language === "da" ? "Telefon" : "Phone"}
                            value={editPhone}
                            onChange={(e) => setEditPhone(e.currentTarget.value)}
                        />
                        <TextInput
                            label={language === "da" ? "Fødselsdato" : "Date of Birth"}
                            type="date"
                            value={editDateOfBirth || ""}
                            onChange={(e) => setEditDateOfBirth(e.currentTarget.value)}
                        />
                        <Select
                            label={language === "da" ? "Land" : "Country"}
                            placeholder={language === "da" ? "Vælg land" : "Select country"}
                            value={editCountry || ""}
                            onChange={setEditCountry}
                            data={countries.map(c => ({ value: c.code, label: c.name || c.code }))}
                        />

                        <Stack gap={8}>
                            <Text fw={700} size="sm">
                                {language === "da" ? "Profilbillede" : "Profile picture"}
                            </Text>

                            {avatarPreview ? (
                                <Image src={avatarPreview} alt="avatar preview" radius="md" h={140} fit="cover" />
                            ) : (
                                <Text c="dimmed" size="sm">
                                    {language === "da" ? "Ingen billede valgt." : "No image selected."}
                                </Text>
                            )}

                            <Group>
                                <FileButton onChange={handleAvatarChange} accept="image/png,image/jpeg,image/webp">
                                    {(props) => (
                                        <Button {...props} variant="light" color="gray">
                                            {language === "da" ? "Vælg billede" : "Choose image"}
                                        </Button>
                                    )}
                                </FileButton>

                                {editAvatar && (
                                    <Button
                                        variant="subtle"
                                        color="gray"
                                        onClick={() => {
                                            setEditAvatar("");
                                            setAvatarPreview(currentAvatarUrl || toImageUrl(userAvatarUrlSession || ""));
                                        }}
                                    >
                                        {language === "da" ? "Fortryd" : "Remove"}
                                    </Button>
                                )}
                            </Group>
                        </Stack>

                        <Group justify="flex-end" mt="sm">
                            <Button color="red" onClick={() => saveProfile(false, true)}>
                                {language === "da" ? "Gem" : "Save"}
                            </Button>
                        </Group>
                    </Stack>
                </Modal>
            </div>
        </div>
    );
};

export default EventUserDashboard;